import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getAccessibleWarehouseIds } from "../../../../../../../helpers/warehousePermissions";
import useWarehouseChange, { locationName } from "./useWarehouseChange";
import { SHOW_FULFILMENT_LOCATION } from "./showFulfilmentLocation";
import "./FulfilmentLocation.css";

const WAREHOUSE_ICON =
  "M22 21V7L12 3 2 7v14h5v-9h10v9h5zm-11-2H9v2h2v-2zm2-3h-2v2h2v-2zm2 3h-2v2h2v-2z";
const CHECK_ICON = "M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z";

const Icon = ({ d }) => (
  <svg className="fulloc__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d={d} />
  </svg>
);

// "1 Warehouse Way" / "Mississauga, Ontario L5T2N7", skipping empty parts.
const addressLines = (warehouse) => {
  const regionLine = [warehouse?.state, warehouse?.postcode].filter(Boolean).join(" ");
  return [
    warehouse?.address,
    [warehouse?.city, regionLine].filter(Boolean).join(", "),
  ].filter(Boolean);
};

// The warehouse choice (StepCheck.Warehouses) drawn as one tile per approved
// warehouse. It records the same value and makes the same writes as the
// Warehouse dropdown My Products had before (removed 2026-10-17; this card is
// now the only place to choose). Nothing is chosen on load, as before.
// "Default" marks the user's own warehouses, as Orders and Settings do
// (warehouse.current_user_id === the account owner), with no permission check.
export default function FulfilmentLocation({
  StepCheck,
  SetStepCheck,
  Plist,
  SetPlist,
  SelectedProducts,
  setSelectedProducts,
}) {
  const user = useSelector((state) => state.user);
  const { requestChange, confirmDialog } = useWarehouseChange({
    Warehouses: StepCheck["Warehouses"],
    SelectedProducts,
    Plist,
    setSelectedProducts,
    SetPlist,
    SetStepCheck,
  });

  // A product clicked before a location is chosen brings the user here.
  const cardRef = useRef(null);
  const groupRef = useRef(null);
  const [attention, setAttention] = useState(false);
  useEffect(() => {
    let timer;
    const show = () => {
      const card = cardRef.current;
      if (!card) return;
      // The card's top (title and first tiles) lands below the navbar, even
      // when it is taller than the screen; no animation for reduced motion.
      // Focus goes to the group, not a tile, so a stray Space picks nothing;
      // Tab then reaches the tiles.
      const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
      card.scrollIntoView?.({ behavior: reduce ? "auto" : "smooth", block: "start" });
      groupRef.current?.focus({ preventScroll: true });
      setAttention(true);
      clearTimeout(timer);
      timer = setTimeout(() => setAttention(false), 1600);
    };
    window.addEventListener(SHOW_FULFILMENT_LOCATION, show);
    return () => {
      window.removeEventListener(SHOW_FULFILMENT_LOCATION, show);
      clearTimeout(timer);
    };
  }, []);

  const approved = Array.isArray(user?.user_approved_warehouses)
    ? user.user_approved_warehouses
    : [];
  // An open question stays on screen even if the list empties meanwhile.
  if (!approved.length) return confirmDialog;

  const ownIds = getAccessibleWarehouseIds(user);
  const selected = StepCheck["Warehouses"];
  const hasSelection = selected !== "" && selected !== null && selected !== undefined;
  const isSelected = (entry) => hasSelection && String(entry?.warehouse_id) === String(selected);
  const current = approved.find(isSelected);
  const nameOf = locationName;

  return (
    <div className="service__card_box_ship_page main__service_card_wrap fulloc__wrap">
      <div
        ref={cardRef}
        className={`card bg-ui-surface shadow wapper_like_shopify fulloc__card${
          attention ? " fulloc__card--attention" : ""
        }`}
      >
        <div className="card-body service__card_box_ship_body">
          <div className="fulloc__title" id="fulfilmentLocationTitle">
            Fulfilment location <span className="fulloc__hint">pick one</span>
          </div>

          <div
            ref={groupRef}
            className="fulloc__tiles"
            role="radiogroup"
            aria-labelledby="fulfilmentLocationTitle"
            tabIndex={-1}
          >
            {approved.map((entry) => {
              const id = entry?.warehouse_id;
              const on = isSelected(entry);
              // The code is shown next to a name; without a name it is the name.
              const code = entry?.warehouse?.name ? entry?.warehouse?.code : "";
              const isOwn = ownIds.has(String(id));
              return (
                <button
                  key={entry?.id ?? id}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  className="fulloc__tile"
                  onClick={() => {
                    // Same rule as the old MUI dropdown: picking the value
                    // that is already set does nothing.
                    if (id === selected) return;
                    requestChange(id);
                  }}
                >
                  <span className="fulloc__tile-ic">
                    <Icon d={WAREHOUSE_ICON} />
                  </span>
                  <span className="fulloc__tile-txt">
                    <b className="fulloc__tile-name">{nameOf(entry)}</b>
                    {/* code and Default always on their own line, so every
                        tile has the same shape whatever the name's length */}
                    {(code || isOwn) && (
                      <span className="fulloc__tile-meta">
                        {code && <span className="fulloc__tile-code">#{code}</span>}
                        {isOwn && <span className="fulloc__tag">Default</span>}
                      </span>
                    )}
                    {addressLines(entry?.warehouse).map((line, index) => (
                      <small key={index}>{line}</small>
                    ))}
                  </span>
                  <span className="fulloc__check">
                    <Icon d={CHECK_ICON} />
                  </span>
                </button>
              );
            })}
          </div>

          <div className="fulloc__foot">
            {current
              ? `Products are fulfilled from ${nameOf(current)}. Changing it removes the products and packages you added.`
              : "Select a location to add products from it."}
          </div>
        </div>
      </div>
      {confirmDialog}
    </div>
  );
}
